import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { isAxiosError } from 'axios';

import { conns } from '@cfg/axiosConfig.tsx';
import type { RootState } from '@store/store';
import { parseUserSearchQuery } from '@tools/search/userSearch.ts';

export interface CustomerPhone {
  id?: number;
  label?: string | null;
  number: string;
  country_code?: string | null;
  is_primary?: boolean | null;
}

export interface CustomerAddress {
  id?: number;
  label?: string | null;
  street: string;
  city?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?: string | null;
  notes?: string | null;
  phone_number?: string | null;
}

export interface CustomerSocialLinks {
  facebook?: string | null;
  instagram?: string | null;
  tiktok?: string | null;
  youtube?: string | null;
  whatsapp?: string | null;
  website?: string | null;
}

export interface CustomerRelationshipResolutionPayload {
  existing_user_id: number;
  relationship_type_id: number;
  is_binding_from_new_to_existing: boolean;
  is_binding_from_existing_to_new: boolean;
}

export interface CustomerRelationshipTypeOption {
  id: number;
  code: string;
  label: string;
  inverse_id?: number | null;
}

export interface CustomerPhoneConflictMatch {
  id: number;
  first_name: string;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface CustomerPhoneConflictError {
  code: 'phone_conflict_requires_relationship';
  message: string;
  matches: CustomerPhoneConflictMatch[];
  relationship_types: CustomerRelationshipTypeOption[];
}

export interface Customer {
  id: number;
  first_name: string;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  phone_country?: string | null;
  document_type?: string | null;
  document_number?: string | null;
  avatar_url?: string | null;
  notes?: string | null;
  phones?: CustomerPhone[] | null;
  addresses?: CustomerAddress[] | null;
  social_links?: CustomerSocialLinks | null;
}

export interface CustomerCreatePayload extends Omit<Customer, 'id'> {
  phones?: CustomerPhone[] | null;
  addresses?: CustomerAddress[] | null;
  social_links?: CustomerSocialLinks | null;
  relationships_to_existing?: CustomerRelationshipResolutionPayload[] | null;
}

type CustomerUpdatePayload = Partial<CustomerCreatePayload>;
type PosCustomersRejectValue = string | CustomerPhoneConflictError;

interface PosCustomersState {
  items: Customer[];
  status: 'idle' | 'loading' | 'failed';
}

const initialState: PosCustomersState = {
  items: [],
  status: 'idle',
};

export interface CustomerSearchParams {
  q: string;
  exact_id?: boolean;
  id_prefix?: boolean;
}

const normalizeCustomerSearchParams = (
  params: string | CustomerSearchParams,
): CustomerSearchParams => {
  const raw = typeof params === 'string' ? { q: params } : params;
  const parsed = parseUserSearchQuery(raw.q);

  if (parsed.mode === 'exact_id') {
    return { q: parsed.value, exact_id: true };
  }
  if (parsed.mode === 'prefix_id') {
    return { q: parsed.value, id_prefix: true };
  }

  return { ...raw, q: parsed.mode === 'normal' ? parsed.tokens.join(' ') : '' };
};

const extractRejectValue = (error: unknown, fallback: string): PosCustomersRejectValue => {
  if (!isAxiosError(error)) {
    return fallback;
  }
  const detail = error.response?.data?.detail;
  if (detail && typeof detail === 'object') {
    const code = (detail as { code?: unknown }).code;
    if (code === 'phone_conflict_requires_relationship') {
      return detail as CustomerPhoneConflictError;
    }
    const message = (detail as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }
  if (typeof detail === 'string') {
    return detail;
  }
  return error.message || fallback;
};

export const searchCustomers = createAsyncThunk<Customer[], string | CustomerSearchParams>(
  'posCustomers/search',
  async (params, { rejectWithValue }) => {
    try {
      const normalized = normalizeCustomerSearchParams(params);
      if (!normalized.q) {
        return [];
      }

      const searchParams: Record<string, string | boolean> = { q: normalized.q };

      if (normalized.exact_id) {
        searchParams.exact_id = true;
      }
      if (normalized.id_prefix) {
        searchParams.id_prefix = true;
      }

      const { data } = await conns<Customer[]>('erp:customers:search').get(searchParams);
      return data;
    } catch (err) {
      return rejectWithValue(`Failed to search customers: ${err}`);
    }
  },
);

export const fetchCustomer = createAsyncThunk<Customer, number>(
  'posCustomers/fetchOne',
  async (customerId, { rejectWithValue }) => {
    try {
      const { data } = await conns<Customer>('erp:customers:detail', [customerId]).get();
      return data;
    } catch (err) {
      return rejectWithValue(`Failed to fetch customer ${customerId}: ${err}`);
    }
  },
);

export const createCustomer = createAsyncThunk<
  Customer,
  CustomerCreatePayload,
  { rejectValue: PosCustomersRejectValue }
>(
  'posCustomers/create',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await conns<Customer>('erp:customers:create').post(payload);
      return data;
    } catch (err) {
      return rejectWithValue(extractRejectValue(err, 'Failed to create customer'));
    }
  },
);

export const updateCustomer = createAsyncThunk<
  Customer,
  { id: number; payload: CustomerUpdatePayload },
  { rejectValue: PosCustomersRejectValue }
>(
  'posCustomers/update',
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const { data } = await conns<Customer>('erp:customers:update', [id]).patch(payload);
      return data;
    } catch (err) {
      return rejectWithValue(extractRejectValue(err, `Failed to update customer ${id}`));
    }
  },
);

export const deleteCustomer = createAsyncThunk<number, number>(
  'posCustomers/delete',
  async (customerId, { rejectWithValue }) => {
    try {
      await conns('erp:customers:delete', [customerId]).delete();
      return customerId;
    } catch (err) {
      return rejectWithValue(`Failed to delete customer ${customerId}: ${err}`);
    }
  },
);

const upsertCustomer = (state: PosCustomersState, customer: Customer) => {
  const index = state.items.findIndex((item) => item.id === customer.id);
  if (index >= 0) {
    state.items[index] = customer;
  } else {
    state.items.unshift(customer);
  }
};

const posCustomersSlice = createSlice({
  name: 'posCustomers',
  initialState,
  reducers: {
    clear(state) {
      state.items = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchCustomers.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(searchCustomers.fulfilled, (state, action) => {
        state.items = action.payload;
        state.status = 'idle';
      })
      .addCase(searchCustomers.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(fetchCustomer.fulfilled, (state, action) => {
        upsertCustomer(state, action.payload);
      })
      .addCase(createCustomer.fulfilled, (state, action) => {
        upsertCustomer(state, action.payload);
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        upsertCustomer(state, action.payload);
      })
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        state.items = state.items.filter((item) => item.id !== action.payload);
      });
  },
});

export const { clear } = posCustomersSlice.actions;
export const selectPosCustomers = (state: RootState) => state?.posCustomers?.items ?? [];
export default posCustomersSlice.reducer;
